import { CommonModule } from '@angular/common';
import { Component, NgZone, OnDestroy, OnInit, inject } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { VW_ERROR_CODES } from 'vwappservices';
import { ReportsApiService } from '../../services/reports-api.service';
import { LiveUpdatesService } from '../../services/live-updates.service';
import { CamelToTitlePipe } from '../../shared/camel-to-title.pipe';
import { reportApiError } from '../../shared/api-result.helper';

interface ActivityEntry {
  _id: string;
  productName: string;
  stepName: string;
  operatorName: string;
  mode: string;
  createdAt: string;
  endTime?: string;
}

interface StepGroup {
  stepName: string;
  entries: ActivityEntry[];
}

interface ProductGroup {
  productName: string;
  totalCount: number;
  steps: StepGroup[];
}

@Component({
  selector: 'app-live-activity',
  standalone: true,
  imports: [CommonModule, CamelToTitlePipe],
  templateUrl: './live-activity.component.html',
  styleUrl: './live-activity.component.scss',
})
export class LiveActivityComponent implements OnInit, OnDestroy {
  entries: ActivityEntry[] = [];
  loading = true;
  lastUpdatedAt: Date | null = null;

  // Ticks every 30s purely to force relative-time labels ("2m ago") to re-render as time passes.
  private clockTick: ReturnType<typeof setInterval> | undefined;
  private toastr = inject(ToastrService);
  private unsubscribeLiveUpdates?: () => void;

  constructor(
    private reportsApi: ReportsApiService,
    private liveUpdates: LiveUpdatesService,
    private ngZone: NgZone,
  ) {}

  async ngOnInit() {
    await this.load();
    this.unsubscribeLiveUpdates = this.liveUpdates.connect({
      'production:update': (data: any) => {
        this.entries = [data.entry, ...this.entries].slice(0, 100);
        this.lastUpdatedAt = new Date();
      },
    });
    this.clockTick = setInterval(() => {}, 30000);
  }

  ngOnDestroy() {
    this.unsubscribeLiveUpdates?.();
    if (this.clockTick) clearInterval(this.clockTick);
  }

  async load() {
    this.loading = true;
    const res = await this.reportsApi.getReport({});
    // Same zone-continuation issue documented in DashboardComponent/SettingsComponent's load() —
    // without this, `loading` flips to false internally but the view stays stuck.
    this.ngZone.run(() => {
      if (res.errorCode === VW_ERROR_CODES.SUCCESS) {
        this.entries = res.Data;
        this.lastUpdatedAt = new Date();
      } else {
        reportApiError(this.toastr, res);
      }
      this.loading = false;
    });
  }

  get latestEntries(): ActivityEntry[] {
    return this.entries.slice(0, 15);
  }

  get activeProductCount(): number {
    return new Set(this.entries.map((e) => e.productName)).size;
  }

  groupedEntries(): ProductGroup[] {
    const order: string[] = [];
    const productSteps = new Map<string, Map<string, ActivityEntry[]>>();

    for (const entry of this.entries) {
      if (!productSteps.has(entry.productName)) {
        order.push(entry.productName);
        productSteps.set(entry.productName, new Map());
      }
      const stepMap = productSteps.get(entry.productName)!;
      if (!stepMap.has(entry.stepName)) stepMap.set(entry.stepName, []);
      stepMap.get(entry.stepName)!.push(entry);
    }

    return order.map((productName) => {
      const stepMap = productSteps.get(productName)!;
      const steps = Array.from(stepMap.entries()).map(([stepName, stepEntries]) => ({ stepName, entries: stepEntries }));
      const totalCount = steps.reduce((sum, s) => sum + s.entries.length, 0);
      return { productName, totalCount, steps };
    });
  }

  formatTime(iso: string): string {
    return new Date(iso).toLocaleTimeString();
  }

  relativeTime(iso: string): string {
    const diffMs = Date.now() - new Date(iso).getTime();
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return 'Just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffHr = Math.floor(diffMin / 60);
    if (diffHr < 24) return `${diffHr}h ago`;
    return `${Math.floor(diffHr / 24)}d ago`;
  }
}
